import { useLayoutEffect, useRef, useState } from "react";
import {
  STAMP_IMG, A4_W, A4_H, A4_BODY_H, stampImgStyle, splitBlocks, renderInline, blockStyle,
} from "@/components/PDFPreviews";

// Inner margins of the printed area, in the sheet's own pixels. Fixed rather than responsive
// because pagination is measured against them: if the padding changed with the viewport, the same
// document would break onto different pages on a phone than on a desktop.
const PAD_X = 56;
const PAD_Y = 40;
const TEXT_W = A4_W - PAD_X * 2;
const TEXT_H = A4_BODY_H - PAD_Y * 2;
// Only the **first** sheet is the pre-printed stamp paper — a real multi-page stamp document
// continues on ordinary plain paper behind it, same as backend/core/pdf.py's two page templates
// (`TOP_MARGIN` vs `TOP_MARGIN_LATER`). A continuation page has no header band to leave clear, so
// it gets the whole sheet height back, padded the same PAD_Y on top as it already is on the bottom.
const TEXT_H_LATER = A4_H - PAD_Y * 2;

// Below this the 13px body renders at single-digit pixels, so "fit width" stops being a useful
// default and the toggle is offered instead. It no longer *clamps* the fit — clamping is what
// pushed the right-hand third of every sheet off a phone screen, so an upright phone showed a
// document whose right margin simply wasn't there and a landscape one showed it fine.
const SMALL_SCALE = 0.6;

/** A block's plain text, for height measurement only — tags are stripped rather than interpreted,
 *  since the hidden host is a bare div and not a React tree. `<br>` still counts as a line break;
 *  everything else (the `<b>`/`<i>`/`<u>` `renderInline` will honour in the real render) only ever
 *  changes width within a line, which this component's line-by-line CSS wrapping already absorbs
 *  close enough for pagination purposes — this was already an approximation, not a promise to
 *  break pages in the exact place ReportLab does server-side. */
const blockPlainText = (text) =>
  String(text || "").replace(/<\s*br\s*\/?\s*>/gi, "\n").replace(/<\/?[a-zA-Z][^<>]*>/g, "");

/** One block's height in its own style, via the shared hidden host. Tags are stripped to plain
 *  text first (see blockPlainText) — the host is a bare div, not a React tree, so this measures
 *  width/wrapping close enough for pagination without needing to mount rich content into it. */
function measureBlockHeight(host, block) {
  const style = blockStyle(block.kind, block.align);
  Object.assign(host.style, style);
  host.textContent = blockPlainText(block.text) || " ";
  // `scrollHeight` is the content box only — a block's own `marginBottom` (the gap between
  // paragraphs, see BODY_CSS) never shows up in it, so it has to be added back by hand or the
  // accumulator in flowIntoPages would under-count every block's real footprint by that margin.
  return host.scrollHeight + (style.marginBottom || 0);
}

/**
 * Character-level fallback for the one block whose own content cannot fit a single page even by
 * itself — splits its *plain* text the same way this component used to flow the whole document,
 * so a pathologically long paragraph still spans multiple pages instead of overflowing off the
 * bottom. This loses `<b>`/`<i>`/`<u>` styling for just that one block; every block in every
 * template shipped with this app is short enough to never take this path.
 */
function splitOversizedBlock(block, host, pageHeight) {
  Object.assign(host.style, blockStyle(block.kind, block.align));
  const fits = (s) => { host.textContent = s.length ? s : " "; return host.scrollHeight <= pageHeight; };
  const chunks = [];
  let rest = blockPlainText(block.text);
  for (let guard = 0; rest.length && guard < 400; guard++) {
    if (fits(rest)) { chunks.push(rest); rest = ""; break; }
    let lo = 0, hi = rest.length;
    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2);
      if (fits(rest.slice(0, mid))) lo = mid; else hi = mid - 1;
    }
    let cut = lo;
    if (cut <= 0) {
      cut = 1;                              // nothing fits at all — force progress
    } else {
      // Only accept a boundary in the back half of the page; otherwise a paragraph-shaped gap
      // near the top would leave most of the sheet blank.
      const head = rest.slice(0, cut);
      const floor = Math.floor(cut * 0.5);
      const nl = head.lastIndexOf("\n");
      const sp = head.lastIndexOf(" ");
      if (nl > floor) cut = nl + 1;
      else if (sp > floor) cut = sp + 1;
    }
    chunks.push(rest.slice(0, cut).replace(/\s+$/, ""));
    rest = rest.slice(cut).replace(/^\n/, "");
  }
  if (rest.length) chunks.push(rest);
  return chunks.map((text) => ({ kind: "p", text, align: block.align }));
}

/**
 * Flow blocks (see `splitBlocks` in PDFPreviews.jsx) into page-sized groups, by measuring each
 * block's own rendered height and accumulating greedily.
 *
 * **Block-level, not character-level.** The body may now carry real markup (`<h1>`-`<h6>`, `<b>`/`<i>`/
 * `<u>`, see backend/CLAUDE.md "Templates"), and a cut has to land between whole blocks — cutting
 * mid-block could split a tag in half, or separate an opening `<b>` from its own closing one onto
 * different pages. Paragraph boundaries were already where this preferred to cut in the old
 * character-level version too; this just makes that the only place it can.
 *
 * `pageHeightFor(pageIndexWithinThisRun)` rather than one flat number — a `\f`-delimited run may
 * itself span several pages, and only the very first page of the *whole document* is the stamp
 * sheet (see `isDocumentStart` at the call site); every other page, whichever run it belongs to,
 * gets the taller plain-paper height.
 */
function flowIntoPages(blocks, host, pageHeightFor) {
  const pages = [];
  let current = [];
  let used = 0;
  const push = () => { if (current.length) pages.push(current); current = []; used = 0; };
  for (const block of blocks) {
    const limit = pageHeightFor(pages.length);
    const h = measureBlockHeight(host, block);
    if (h > limit) {
      push();
      for (const sub of splitOversizedBlock(block, host, pageHeightFor(pages.length))) pages.push([sub]);
      continue;
    }
    if (used + h > limit && current.length) push();
    current.push(block);
    used += h;
  }
  push();
  return pages.length ? pages : [[]];
}

/**
 * Read-only rendering of a document's text on stamp-paper-shaped sheets.
 *
 * **The stamp header is a viewing affordance, not part of the document.** The real article is
 * printed onto pre-printed non-judicial stamp paper that already carries the Government of Tamil
 * Nadu header, so the generated PDF is body text alone (see backend/core/pdf.py). The image here
 * exists so the reader sees what the finished sheet will look like. **Only page 1** shows it —
 * only the first physical sheet is that special pre-printed paper; a case that runs long
 * continues on ordinary plain paper behind it, so pages 2+ get an ordinary top margin and more
 * usable height instead of wasting the same band on paper that was never printed there.
 *
 * **Pages are found by measurement, not just by `\f`.** `\f` is the explicit break the admin
 * editor writes, and it is still honoured — but a body with no breaks in it that runs past one
 * sheet used to be clipped by `overflow-hidden`, so the customer saw page 1 of a rental agreement
 * and nothing else. Each `\f`-delimited run is now flowed across as many sheets as it needs, at
 * the granularity of a whole paragraph/heading (a "block", see `splitBlocks` in PDFPreviews.jsx)
 * rather than a character — a body may carry real markup (`<h1>`-`<h6>`, `<b>`/`<i>`/`<u>`) since
 * templates gained a small HTML allow-list, and a cut can never land inside a tag if it only ever
 * lands between whole blocks.
 *
 * Sheets are laid out at true A4 pixels and scaled down to fit narrow screens, rather than being
 * reflowed. Reflowing would repaginate the document per viewport — the same agreement would be 3
 * pages on a desktop and 5 on a phone, and neither would be what comes off the printer.
 *
 * Used by the customer's case page and the wizard's live preview. Admin edits the same text in
 * AdminDocumentEditor — the customer may read it but never change it: their own fields lock at
 * payment and corrections travel as messages (backend/LIFECYCLE.md "Who can edit the document").
 *
 * **The body text is deliberately not selectable or copyable** — `user-select: none` plus a
 * `copy` handler that blocks it outright, on the rendered text only (not the stamp image, which
 * was never selectable anyway, and not the rest of the page — the form fields, buttons and
 * everything outside this sheet behave normally). This is friction, not real protection: page
 * source, devtools and a screenshot all still get the text out, same as on any web page. It stops
 * the one-click "select all, copy, paste elsewhere" path a customer would otherwise have before
 * ever paying, which is the actual thing this is for.
 */
export default function DocumentSheet({ body, extraClauses, title, fit = "width" }) {
  const [pages, setPages] = useState(null);
  // The room the sheets actually have. Height matters only when `fit="container"`.
  const [box, setBox] = useState({ w: A4_W, h: 0 });
  // "Actual size" — the reader's explicit choice to trade seeing the whole page for larger text,
  // at which point the area scrolls. Off by default: seeing the page whole is the more useful
  // default, and a phone can pinch-zoom whatever it needs to read closely.
  const [actualSize, setActualSize] = useState(false);
  const sheetsRef = useRef(null);
  // `container` fits the sheet to the space it has in *both* directions. The wizard's preview
  // column is a fixed-height pane beside the form, and fitting width alone made an A4 sheet
  // (1:1.41) about 150px taller than the pane — so the first page always ran off the bottom and
  // had to be scrolled, which is the one thing a live preview must not require.
  const fitToBox = fit === "container";

  const source = String(body || "") +
    (extraClauses ? `\n\nADDITIONAL CLAUSES\n${extraClauses}` : "");

  useLayoutEffect(() => {
    let cancelled = false;
    const measure = () => {
      if (cancelled) return;
      const host = document.createElement("div");
      Object.assign(host.style, blockStyle("p"), {
        position: "absolute", left: "-99999px", top: "0",
        width: `${TEXT_W}px`, visibility: "hidden", pointerEvents: "none",
      });
      document.body.appendChild(host);
      try {
        // Honour the admin's explicit breaks first, then flow each run's blocks within them. Only
        // the very first page of the very first run is the overall document's page 1 (the stamp
        // sheet, TEXT_H) — every other page, whether later in this run or in a later one, is a
        // plain continuation page (TEXT_H_LATER), matching backend/core/pdf.py's two page
        // templates exactly.
        const out = [];
        source.split("\f").map(splitBlocks).forEach((blocks, runIndex) => {
          const pageHeightFor = (pageIndexInRun) =>
            runIndex === 0 && pageIndexInRun === 0 ? TEXT_H : TEXT_H_LATER;
          out.push(...flowIntoPages(blocks, host, pageHeightFor));
        });
        if (!cancelled) setPages(out);
      } finally {
        host.remove();
      }
    };
    measure();
    // Re-measure once webfonts land — measuring against a fallback face gives the wrong page count.
    if (document.fonts?.ready) document.fonts.ready.then(measure).catch(() => {});
    return () => { cancelled = true; };
  }, [source]);

  // Measure the sheet area — not the whole component, whose height includes the control row.
  // No floor on the result: a floor meant a container narrower than 60% of A4 (any phone held
  // upright) got a sheet wider than itself, and the overflow took the right-hand side off screen.
  useLayoutEffect(() => {
    const el = sheetsRef.current;
    if (!el) return;
    const measure = () => setBox({ w: el.clientWidth || A4_W, h: el.clientHeight || 0 });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const list = pages || [source.split("\f").flatMap(splitBlocks)];
  const byWidth = Math.min(1, (box.w || A4_W) / A4_W);
  // No page-label row to budget for any more (see below) — the sheet gets the box's full height,
  // which is the point: every pixel here should be a pixel of document, not chrome above it.
  const fitScale = fitToBox && box.h > 0 ? Math.min(byWidth, box.h / A4_H) : byWidth;
  const scale = actualSize ? 1 : fitScale;
  // Nothing to choose between when the sheet already fits at full size.
  const canZoom = fitScale < 1;

  return (
    <div className={`flex flex-col min-w-0 ${fitToBox ? "h-full min-h-0" : ""}`}
         data-testid="document-sheet" data-pages={list.length} data-scale={scale.toFixed(2)}>
      {/* Title and zoom control share one row. They were a heading *and* a separate control row,
          two lines of chrome above a preview that was already short of height. */}
      {(title || canZoom) && (
        <div className="flex items-center justify-between gap-3 shrink-0 mb-2 text-[10px] uppercase tracking-[0.24em] text-[#6B7280]">
          <span className="truncate">{title}</span>
          {canZoom && (
            <div className="flex items-center gap-2 shrink-0">
              {actualSize && <span className="normal-case tracking-normal hidden sm:inline">Scroll to read</span>}
              {!actualSize && fitScale < SMALL_SCALE && (
                <span className="normal-case tracking-normal hidden sm:inline">Pinch to zoom</span>
              )}
              <button
                data-testid="document-zoom-toggle" onClick={() => setActualSize(z => !z)}
                className="border border-white/15 rounded-full px-3 py-1 hover:text-white hover:border-white/30 transition-colors"
              >
                {actualSize ? (fitToBox ? "Fit page" : "Fit width") : "Actual size"}
              </button>
            </div>
          )}
        </div>
      )}
      {/* Fitted to a box, this area owns the scrolling (page 2 onward). Fitted to width only, it
          has no height of its own and the page around it scrolls, as before. */}
      <div ref={sheetsRef} className={`space-y-6 ${fitToBox ? "flex-1 min-h-0 overflow-auto" : "overflow-x-auto"}`}>
      {list.map((blocks, i) => (
        <div key={i}>
          {/* Outer box owns the scaled-down footprint so the sheets don't leave a gap beneath —
              and takes the scaled *width* too, so a sheet limited by height sits centred instead
              of hugging the left edge with all its spare room in one margin. */}
          <div style={{ width: A4_W * scale, height: A4_H * scale, overflow: "hidden", marginInline: "auto" }}>
            <div
              data-testid={`document-page-${i}`}
              className="bg-[#FEFDF8] border border-[#E8DFC7] shadow-[0_4px_24px_rgba(10,25,47,0.10)] overflow-hidden"
              style={{ width: A4_W, height: A4_H, transform: `scale(${scale})`, transformOrigin: "top left" }}
            >
              {/* Only page 1 is the pre-printed stamp sheet — a real multi-page document
                  continues on ordinary plain paper behind it, so the header image (and the room
                  it reserves) belongs to page 1 alone. Mirrors backend/core/pdf.py's two page
                  templates (TOP_MARGIN vs TOP_MARGIN_LATER). */}
              {i === 0 && (
                <img src={STAMP_IMG} alt="Government of Tamil Nadu · Non-Judicial Stamp Paper"
                     className="w-full block select-none" style={stampImgStyle} draggable={false}/>
              )}
              <div
                data-testid={`document-page-${i}-body`}
                onCopy={(e) => e.preventDefault()}
                onContextMenu={(e) => e.preventDefault()}
                style={{
                  height: i === 0 ? A4_BODY_H : A4_H, padding: `${PAD_Y}px ${PAD_X}px`, color: "#112240",
                  userSelect: "none", WebkitUserSelect: "none", MozUserSelect: "none",
                }}
              >
                {/* One block per template paragraph/heading (see splitBlocks) — each carries its
                    own style so a heading renders bold+larger (h1 also centred) without affecting the plain
                    paragraphs around it. renderInline turns the allow-listed `<b>`/`<i>`/`<u>`
                    into real elements, never raw HTML — see PDFPreviews.jsx. */}
                {blocks.map((b, j) => (
                  <div key={j} style={blockStyle(b.kind, b.align)}>{renderInline(b.text)}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ))}
      </div>
    </div>
  );
}
